import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useContext,
} from 'react';
import MuiTextField from '@material-ui/core/TextField';
import MuiInputAdornment from '@material-ui/core/InputAdornment/InputAdornment';
import MuiSearchIcon from '@material-ui/icons/Search';
import MuiPaper from '@material-ui/core/Paper';
import MuiList from '@material-ui/core/List';
import MuiListItem from '@material-ui/core/ListItem';
import MuiListItemText from '@material-ui/core/ListItemText';
import MuiCircularProgress from '@material-ui/core/CircularProgress';

import L from 'leaflet';
import { useEventCallback } from '@eas/common-web';
import { useStyles } from './map-styles';
import { MapContext } from './map-ctx';
import { MAP_API_KEY } from './layers';
import {
  MapyGeocodeResult,
  MapyGeocodeResultEntity,
  MapyGeocodeEntityType,
} from './map-suggest-types';

/**
 * Determines an appropriate map zoom level based on the geocoded entity type, when bbbox is not availible.
 * @param type The type of the geocoded entity.
 */
function getZoomByGeocodeEntityType(type: MapyGeocodeEntityType): number {
  switch (type) {
    case 'regional.country':
      return 7; // Adjusted for broader view
    case 'regional.region':
      return 9; // Adjusted
    case 'regional.municipality':
    case 'regional.municipality_part':
      return 12;
    case 'regional.street':
      return 16; // More zoom for streets
    case 'regional.address':
      return 18; // High zoom for specific addresses
    case 'poi':
      return 17;
    case 'coordinate':
      return 18;
    case 'regional':
      return 10;
    default:
      return 14; // General fallback
  }
}

export function MapSuggest() {
  const classes = useStyles();
  const { map } = useContext(MapContext);

  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<MapyGeocodeResultEntity[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1); // For keyboard navigation

  const searchInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSuggestionClick = useEventCallback(
    (item: MapyGeocodeResultEntity) => {
      if (map.current?.ref) {
        if (
          item.bbox &&
          item.bbox.length === 4 &&
          item.bbox.every((coord) => typeof coord === 'number')
        ) {
          // bbox is [minLon, minLat, maxLon, maxLat]
          // Leaflet LatLngBounds is [[southLat, westLng], [northLat, eastLng]]
          const southWest = L.latLng(item.bbox[1], item.bbox[0]); // (minLat, minLon)
          const northEast = L.latLng(item.bbox[3], item.bbox[2]); // (maxLat, maxLon)
          const bounds = L.latLngBounds(southWest, northEast);
          map.current.ref.flyToBounds(bounds);
        } else {
          // Fallback to point and zoom level if no valid bbox
          const { lat, lon } = item.position;
          const zoom = getZoomByGeocodeEntityType(item.type);
          map.current.ref.flyTo([lat, lon], zoom);
        }
        setInputValue(`${item.name}, ${item.location}`);
      }
      setSuggestions([]);
      setShowSuggestions(false);
      setActiveIndex(-1);
    }
  );

  const fetchSuggestions = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setLoading(true);
    // alternativa je  napr https://photon.komoot.io/, ale neni v cestine
    try {
      const response = await fetch(
        `https://api.mapy.cz/v1/suggest?lang=cs&limit=5&type=regional&type=regional.address&apikey=${MAP_API_KEY}&query=${encodeURIComponent(
          query
        )}`
      );
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      const data = (await response.json()) as MapyGeocodeResult;
      setSuggestions(data.items || []);
      setShowSuggestions(data.items && data.items.length > 0);
      setActiveIndex(-1);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setInputValue(query);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (!query.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      setLoading(false);
      return;
    }

    debounceTimeoutRef.current = setTimeout(() => {
      fetchSuggestions(query);
    }, 300);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prevIndex) =>
        prevIndex < suggestions.length - 1 ? prevIndex + 1 : 0
      );
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prevIndex) =>
        prevIndex > 0 ? prevIndex - 1 : suggestions.length - 1
      );
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (activeIndex >= 0 && activeIndex < suggestions.length) {
        handleSuggestionClick(suggestions[activeIndex]);
      }
    } else if (event.key === 'Escape') {
      setShowSuggestions(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <MuiTextField
        inputRef={searchInputRef}
        classes={{ root: classes.searchTextField }}
        autoComplete="off"
        type="search"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (inputValue.trim() && suggestions.length > 0) {
            setShowSuggestions(true);
          }
        }}
        margin="dense"
        placeholder="Vyhledat v mapě"
        variant="outlined"
        InputProps={{
          classes: { root: classes.searchTextFieldInput },
          endAdornment: (
            <MuiInputAdornment position="end">
              {loading ? (
                <MuiCircularProgress size={20} />
              ) : (
                <MuiSearchIcon classes={{ root: classes.searchIcon }} />
              )}
            </MuiInputAdornment>
          ),
          style: {
            boxShadow: 'none',
            paddingRight: 0,
          },
        }}
      />
      {showSuggestions && suggestions.length > 0 && (
        <MuiPaper
          ref={suggestionsRef}
          elevation={3}
          style={{
            display: 'flex',
            flexDirection: 'column',
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: '300px',
          }}
        >
          <div style={{ flex: '1 1 auto', overflowY: 'auto' }}>
            <MuiList dense>
              {suggestions.map((item, index) => (
                <MuiListItem
                  key={`${item.position.lat}-${item.position.lon}-${index}`}
                  button
                  selected={index === activeIndex}
                  onClick={() => handleSuggestionClick(item)}
                >
                  <MuiListItemText
                    primary={item.name}
                    secondary={`${item.label}, ${item.location}`}
                  />
                </MuiListItem>
              ))}
            </MuiList>
          </div>
          <div
            style={{
              padding: '8px',
              textAlign: 'right',
              flexShrink: 0,
              borderTop: '1px solid #eee',
            }}
          >
            Powered by{' '}
            <img
              src="https://api.mapy.cz/img/api/logo-small.svg"
              alt="Mapy.cz logo"
              style={{ verticalAlign: 'middle', width: '60px' }}
            />
          </div>
        </MuiPaper>
      )}
    </div>
  );
}
